import React, { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRoute } from '../../../App.routing';
import { ArrowLeft } from 'lucide-react';
import clsx from 'clsx';

type PageLayoutNewProps = {
    routes: AppRoute[];
    pageName: string;
    name: string | undefined | null;
    subName?: string | undefined | null;
    backPath?: string;
    headerActions?: ReactNode;
    style?: CSSProperties;
    children?: ReactNode;
    className?: string;
};

const PageLayoutNew = (props: PageLayoutNewProps) => {
    const { children, className, pageName, name, subName, routes, backPath, headerActions } = props;
    const [currentRoute, setCurrentRoute] = useState<AppRoute>();
    const location = useLocation();
    const navigate = useNavigate();

    const visibleRoutes = routes.filter(r => r.type === 1 || r.type === 2);

    useEffect(() => {
        setCurrentRoute(
            routes.filter(route => route.path.split("/")[0]?.toLowerCase() === location.pathname.split("/")[3]?.toLowerCase())[0] ??
            routes.filter(route => route.path.split("/")[0]?.toLowerCase() === location.pathname.split("/")[2]?.toLowerCase())[0]
        );
    }, [location.pathname]);

    const isTabActive = (route: AppRoute) => {
        const routeBase = route.path.split("/")[0]?.toLowerCase();
        const pathSegment3 = location.pathname.split("/")[3]?.toLowerCase();
        const pathSegment2 = location.pathname.split("/")[2]?.toLowerCase();
        return routeBase === pathSegment3 || routeBase === pathSegment2;
    };

    return (
        <div className={clsx('flex flex-col h-full', className)}>
            {/* Detail Page Header */}
            <div className="flex-shrink-0 border-b" style={{ borderColor: 'var(--border-default)', backgroundColor: 'var(--bg-card)' }}>
                <div className="page-layout-header">
                    {/* Back button and breadcrumb */}
                    <div className="flex items-center gap-3 mb-4">
                        {backPath && (
                            <button
                                onClick={() => navigate(backPath)}
                                className="btn-icon p-1.5 rounded-lg"
                            >
                                <ArrowLeft size={18} />
                            </button>
                        )}
                        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>
                            {pageName}
                        </span>
                    </div>

                    {/* Title */}
                    <div className="page-layout-title-row" style={{ marginBottom: subName ? 0 : 16 }}>
                        <h1 className="text-xl font-semibold tracking-tight" style={{ color: 'var(--text-primary)', margin: 0 }}>
                            {name}
                        </h1>
                        {headerActions && (
                            <div className="flex items-center gap-2">
                                {headerActions}
                            </div>
                        )}
                    </div>
                    {subName && (
                        <p className="text-sm mb-4" style={{ color: 'var(--text-tertiary)', marginTop: 2 }}>
                            {subName}
                        </p>
                    )}

                    {/* Tab Navigation */}
                    {visibleRoutes.length > 1 && (
                        <div className="tab-bar mb-1" style={{ alignSelf: 'flex-start' }}>
                            {visibleRoutes.map(route => {
                                const active = isTabActive(route);
                                const RouteIcon = route.iconComponent;
                                return (
                                    <button
                                        key={route.path}
                                        onClick={() => navigate(route.path)}
                                        className={active ? 'tab-item-active' : 'tab-item'}
                                    >
                                        {RouteIcon && <RouteIcon size={14} strokeWidth={1.5} />}
                                        {route.name}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                {children}
            </div>
        </div>
    );
};

export default PageLayoutNew;
